import { COUNTRIES, countryLabel } from "@/lib/countries"
import type { ShipmentAddress } from "@/types/shipments"

/**
 * OKS-984 — carrier-ready address formatting for the Shipments queue.
 *
 * Two distinct outputs, because a block you READ and a block you PASTE INTO A
 * CARRIER FORM are not the same artefact:
 *
 *   - {@link addressLines} — for display. Country in the viewer's locale, so a
 *     French operator reads "France".
 *   - {@link addressForClipboard} — for pasting. Country as the full
 *     UPPERCASED ENGLISH name (UX-OQ-5), because carriers reject a bare
 *     two-letter destination and international labels are conventionally
 *     English.
 *
 * ⚠️ These deliberately differ. Making the clipboard reuse the display string
 * would put "Allemagne" on a label bound for Germany.
 */

/**
 * Display lines, in postal order. Blank `street_line_2` collapses with no
 * empty line rather than leaving a gap (UX Spec §3 edge).
 *
 * @param address the gated address payload
 * @param locale  viewer locale for the country name
 */
export function addressLines(
  address: ShipmentAddress,
  locale: "en" | "fr",
): string[] {
  return [
    address.recipient_name,
    address.street,
    address.street_line_2,
    `${address.postal_code} ${address.city}`.trim(),
    countryLabel(address.country, locale),
  ].filter((line): line is string => Boolean(line?.trim()))
}

/**
 * A single newline-joined string for one clipboard write (UX Spec §2.1).
 *
 * ⚠️ Country is `countryLabel(code, "en").toUpperCase()`, NOT the stored ISO-2
 * and NOT the viewer's locale — see the module note. `countryLabel` falls back
 * to the raw code when `Intl.DisplayNames` cannot resolve it, which is the
 * right degradation: an unknown code is still better on a label than an empty
 * line.
 *
 * Postal code and city share a line (`75011 Paris`), which is the format every
 * carrier form in the launch cohort expects.
 */
export function addressForClipboard(address: ShipmentAddress): string {
  return [
    address.recipient_name,
    address.street,
    address.street_line_2,
    `${address.postal_code} ${address.city}`.trim(),
    clipboardCountry(address.country),
  ]
    .filter((line): line is string => Boolean(line?.trim()))
    .join("\n")
}

/**
 * Country line for a carrier label.
 *
 * ⚠️ Resolves the name ONLY for codes in our curated {@link COUNTRIES} list;
 * anything else emits the raw code.
 *
 * The reason is not tidiness. `Intl.DisplayNames` resolves RESERVED codes to
 * placeholder prose — `"ZZ"` becomes **"Unknown Region"**, which would be
 * printed verbatim onto a shipping label. That is strictly worse than an ISO
 * code: a carrier can do nothing with either, but "UNKNOWN REGION" looks like
 * a real destination to whoever pastes it, so the error is discovered at the
 * depot instead of at the keyboard. (Truly unassigned codes like `"QQ"` do
 * fall back to the code, so the failure is specific to reserved ones —
 * exactly the kind of partial behaviour that survives a spot check.)
 *
 * `collab_shipping_addresses.country` only validates `/\A[A-Z]{2}\z/`, so
 * `"ZZ"` is storable today.
 */
function clipboardCountry(code: string): string {
  const known = COUNTRIES.some((country) => country.code === code)
  return known ? countryLabel(code, "en").toUpperCase() : code
}
